import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BRAND, RED, RED_BG } from "@/shared/constants.js";
import { Field } from "@/shared/components/Field.jsx";
import { QuoteStrip } from "@/modules/Quote/components/QuoteStrip.jsx";
import { Spacer } from "@/shared/components/Spacer.jsx";
import Alert from "@/shared/components/Alert.jsx";
import Loader from "@/shared/components/Loader.jsx";
import { btnPrimary, dis, inputBase } from "@/shared/utils/styles.js";

import {
  buildUnderwritingGroupSavePayload,
  hydrateSubmissionAnswers,
  isQuestionVisible as isVisibilityQuestionVisible,
  mergeAnswerMaps,
  recomputeHiddenQuestionIds,
  requiredQuestionsAnswered,
  saveSubmissionQuestionAnswers,
  sortedOptions,
  sortedQuestions,
} from "@/modules/Quote/api/questionsApi.js";
import { fetchInsuredOrderDetails } from "@/modules/Quote/api/quoteApi.js";

import { useStore } from "@/shared/store/useStore.js";
import questionsStore from "@/modules/Quote/store/questionsStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import paymentOrderStore from "@/modules/Payment/store/paymentOrderStore.js";
import { useQuoteSnapshot } from "@/modules/Quote/utils/useQuoteSnapshot.js";

export default function UnderwritingPage() {
  const navigate = useNavigate();

  // ── Store-backed reads ─────────────────────────────────────────────────
  const submissionQuestionGroups   = useStore(questionsStore, (s) => s.submissionQuestionGroups);
  const submissionQuestionsLoading = useStore(questionsStore, (s) => s.submissionQuestionsLoading);
  const submissionQuestionsError   = useStore(questionsStore, (s) => s.submissionQuestionsError);
  const underwritingAnswers        = useStore(questionsStore, (s) => s.underwritingAnswers);
  const impactAnswers              = useStore(questionsStore, (s) => s.impactAnswers);
  const hiddenQuestionIds          = useStore(questionsStore, (s) => s.hiddenQuestionIds);
  const underwritingSaving         = useStore(questionsStore, (s) => s.underwritingSaving);
  const underwritingSaveError      = useStore(questionsStore, (s) => s.underwritingSaveError);
  const flowSubmissionId           = useStore(submissionStore, (s) => s.flowSubmissionId);

  // Derived (premium, snapshot, allAnswered) all from one hook.
  const {
    quoteStripAnnualTotal, snapshotLimits, snapshotPolicyLine, quoteStripAmountPending,
    allAnswered,
  } = useQuoteSnapshot();

  // ── Question group + helpers ───────────────────────────────────────────
  const underwritingGroup = submissionQuestionGroups.find(
    (g) => /underwriting/i.test(String(g?.groupName || "")),
  ) || null;
  const heading = underwritingGroup?.groupName || "Underwriting questions";

  // Recompute the submission-wide hidden set from the whole tree + all live
  // answer maps. Reads directly off the store so it's correct even when called
  // synchronously right after an answer write.
  const recomputeHidden = () => {
    const liveAnswers = mergeAnswerMaps(
      questionsStore.questionAnswers,
      questionsStore.coverageAnswers,
      questionsStore.underwritingAnswers,
    );
    questionsStore.setHiddenQuestionIds(
      recomputeHiddenQuestionIds(
        questionsStore.submissionQuestionGroups,
        liveAnswers,
        questionsStore.showTargetQuestionIds,
      ),
    );
  };

  // Hydrate saved answers + defaults from the submission tree. Merges UNDER
  // current store values so in-progress edits aren't clobbered on re-render.
  useEffect(() => {
    if (!underwritingGroup) return;
    const { answers, impacts } = hydrateSubmissionAnswers(underwritingGroup);
    if (Object.keys(answers).length > 0) {
      questionsStore.underwritingAnswers = { ...answers, ...questionsStore.underwritingAnswers };
    }
    if (Object.keys(impacts).length > 0) {
      questionsStore.impactAnswers = { ...impacts, ...questionsStore.impactAnswers };
    }
    recomputeHidden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [underwritingGroup]);
  const allQuestions = sortedQuestions(underwritingGroup?.questions);
  const yesNoQuestions = allQuestions.filter(
    (q) => (q?.questionType === "YES_NO" || q?.questionType === "RADIO_BUTTON")
      && isVisibilityQuestionVisible(q, hiddenQuestionIds),
  );
  const textBoxQuestions = allQuestions.filter(
    (q) => q?.questionType === "TEXTBOX" && isVisibilityQuestionVisible(q, hiddenQuestionIds),
  );

  const isYesOption = (o) => /^yes/i.test(String(o?.optionValue || o?.optionLabel || ""));
  const isAnyYesSelected = yesNoQuestions.some((q) => {
    const sel = underwritingAnswers[String(q.id)];
    if (sel == null) return false;
    const opt = (q.options || []).find((o) => String(o.id) === String(sel));
    return opt && isYesOption(opt);
  });
  const isOptionSelected = (q, optionId) =>
    String(underwritingAnswers[String(q.id)]) === String(optionId);

  // Continue gate: every yes/no question answered (`allAnswered`) AND every
  // required question satisfied. Follow-up textboxes only render once a "yes"
  // is selected, so they only gate while visible. The pre-existing follow-up
  // rule is combined (logical AND) with server-driven Visibility.
  const isFollowupVisible = (q) => (q?.questionType === "TEXTBOX" ? isAnyYesSelected : true);
  const isVisible = (q) => isFollowupVisible(q) && isVisibilityQuestionVisible(q, hiddenQuestionIds);
  const requiredAnswered = requiredQuestionsAnswered(allQuestions, underwritingAnswers, {
    isVisible,
  });
  const canContinue = allAnswered && requiredAnswered && !underwritingSaving;

  // ── Answer setters (write straight to questionsStore) ──────────────────
  const setRadio = (q, optionId) => {
    questionsStore.underwritingAnswers = {
      ...underwritingAnswers,
      [String(q.id)]: String(optionId),
    };
    recomputeHidden();
  };
  const setText = (q, value) => {
    questionsStore.underwritingAnswers = {
      ...underwritingAnswers,
      [String(q.id)]: value,
    };
  };

  // ── Continue: save underwriting answers, then route to /reviewDocusign ──
  const handleContinue = async () => {
    if (underwritingSaving) return;
    if (!allAnswered || !requiredAnswered) return;
    if (!flowSubmissionId) { navigate("/reviewDocusign"); return; }
    const payload = buildUnderwritingGroupSavePayload(
      flowSubmissionId, submissionQuestionGroups, underwritingAnswers, impactAnswers, hiddenQuestionIds,
    );
    if (!payload) { navigate("/reviewDocusign"); return; }

    questionsStore.underwritingSaveError = null;
    questionsStore.underwritingSaving = true;
    try {
      await saveSubmissionQuestionAnswers(payload);
      // Re-fetch the order so `questionRequired` reflects the just-saved state.
      paymentOrderStore.paymentOrderLoading = true;
      const freshOrder = await fetchInsuredOrderDetails(flowSubmissionId);
      paymentOrderStore.paymentOrderDetails = freshOrder;
      paymentOrderStore.paymentOrderLoading = false;
      if (freshOrder?.questionRequired === true) {
        questionsStore.underwritingSaveError = "Please answer all required questions before proceeding.";
        return;
      }
      navigate("/reviewDocusign");
    } catch (err) {
      questionsStore.underwritingSaveError = err?.message || "Could not save underwriting answers.";
    } finally {
      questionsStore.underwritingSaving = false;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      <QuoteStrip total={quoteStripAnnualTotal} limits={snapshotLimits} claims={snapshotPolicyLine} amountPending={quoteStripAmountPending} amountLabel="Your total" />
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 19, fontWeight: 600, color: "#1a1a1a", margin: "6px 0 4px" }}>{heading}</h2>
      <p style={{ fontSize: 12, color: "#888", marginBottom: 14, lineHeight: 1.5 }}>Within the past 10 years. "Yes" doesn't disqualify you — a specialist will review.</p>

      {submissionQuestionsLoading && <Loader label="Loading questions…" />}
      {submissionQuestionsError && (
        <Alert type="error">Couldn't load questions. {submissionQuestionsError.message}</Alert>
      )}

      {yesNoQuestions.map((q, i) => {
        const qid = String(q.id);
        const opts = sortedOptions(q.options);
        const yesOpt = opts.find(isYesOption);
        const yesSelected = yesOpt && isOptionSelected(q, yesOpt.id);
        return (
          <div key={qid} style={{
            marginBottom: 8, padding: "10px 12px",
            background: yesSelected ? RED_BG : "#fafafa",
            borderRadius: 10, border: `1px solid ${yesSelected ? "#f0c0c0" : "transparent"}`,
            transition: "all 0.2s",
          }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: yesSelected ? RED : "#333", marginBottom: q.questionDescription ? 4 : 8 }}>
              <span style={{ color: "#aaa", fontWeight: 500 }}>{i + 1}.</span> {q.questionText}
              {q.isRequired && <span style={{ color: RED, marginLeft: 4 }}>*</span>}
            </div>
            {q.questionDescription && (
              <div style={{ fontSize: 11, color: "#888", marginBottom: 8, lineHeight: 1.4, paddingLeft: 16 }}>
                {q.questionDescription}
              </div>
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {opts.map((o) => {
                const isYes = isYesOption(o);
                const sel = isOptionSelected(q, o.id);
                const desc = o.optionDescription || o.description || null;
                return (
                  <div key={o.id} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                    <button onClick={() => setRadio(q, o.id)} style={{
                      padding: "6px 18px", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif", border: "none",
                      background: sel ? (isYes ? RED : BRAND) : "#e8e8e6",
                      color: sel ? "#fff" : "#666",
                      transition: "all 0.15s",
                    }}>{o.optionLabel}</button>
                    {desc && (
                      <div style={{ fontSize: 10, color: "#888", lineHeight: 1.4, paddingLeft: 2, fontFamily: "'DM Sans', sans-serif" }}>{desc}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {isAnyYesSelected && textBoxQuestions.map((q) => {
        const qid = String(q.id);
        const opts = q.options || [];
        const labelWithRequired = (
          <>
            {q.questionText}
            {q.isRequired && <span style={{ color: RED, marginLeft: 4 }}>*</span>}
          </>
        );
        return (
          <Field key={qid} label={labelWithRequired}>
            {q.questionDescription && (
              <div style={{ fontSize: 11, color: "#888", marginBottom: 6, lineHeight: 1.4 }}>
                {q.questionDescription}
              </div>
            )}
            <textarea
              value={underwritingAnswers[qid] || ""}
              onChange={(e) => setText(q, e.target.value)}
              placeholder={opts[0]?.optionLabel || "Describe the circumstances..."}
              style={{ ...inputBase, minHeight: 80, resize: "vertical" }}
            />
          </Field>
        );
      })}

      <Spacer />
      <Alert type="error" message={underwritingSaveError} className="alert-center" />

      <button style={dis(btnPrimary, canContinue)} onClick={handleContinue}>
        {underwritingSaving ? "Saving…" : (isAnyYesSelected ? "Submit for review" : "See my final quote")}
      </button>
    </>
  );
}
